// components/PasswordResetModal.tsx
// Drop-in web version — mirrors the React Native PasswordResetModal API

import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface PasswordResetModalProps {
  visible:     boolean;
  onClose:     () => void;
  onSubmit:    (newPassword: string) => Promise<void>;
  studentName: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const REQUIREMENTS = [
  { text: 'At least 6 characters long',    met: (p: string) => p.length >= 6 },
  { text: 'Contains at least one letter',  met: (p: string) => /[a-zA-Z]/.test(p) },
  { text: 'Contains at least one number',  met: (p: string) => /[0-9]/.test(p) },
];

// ─────────────────────────────────────────────────────────────────────────────
// Validators
// ─────────────────────────────────────────────────────────────────────────────
const validatePassword = (p: string): string => {
  if (!p)           return 'Password is required';
  if (p.length < 6) return 'Must be at least 6 characters';
  if (!/(?=.*[a-zA-Z])(?=.*[0-9])/.test(p)) return 'Must contain a letter and a number';
  return '';
};

const validateConfirm = (p: string, c: string): string => {
  if (!c)      return 'Please confirm your password';
  if (p !== c) return 'Passwords do not match';
  return '';
};

// ─────────────────────────────────────────────────────────────────────────────
// Strength helper
// ─────────────────────────────────────────────────────────────────────────────
const getStrength = (p: string): { level: number; label: string; color: string } => {
  if (!p) return { level: 0, label: '', color: '#E5E7EB' };
  let score = 0;
  if (p.length >= 6)             score++;
  if (/[a-zA-Z]/.test(p))       score++;
  if (/[0-9]/.test(p))          score++;
  if (p.length >= 10)            score++;
  if (/[^a-zA-Z0-9]/.test(p))   score++;
  if (score <= 2) return { level: score, label: 'Weak',   color: '#EF4444' };
  if (score === 3) return { level: score, label: 'Fair',   color: '#F59E0B' };
  return              { level: score, label: 'Strong', color: '#22C55E' };
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  visible,
  onClose,
  onSubmit,
  studentName,
}) => {
  const toast = useRef<Toast>(null);

  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPassError,    setNewPassError]    = useState('');
  const [confirmError,    setConfirmError]    = useState('');
  const [isSubmitting,    setIsSubmitting]    = useState(false);
  const [showNew,         setShowNew]         = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);

  // Reset form whenever modal opens
  useEffect(() => {
    if (visible) {
      setNewPassword('');
      setConfirmPassword('');
      setNewPassError('');
      setConfirmError('');
      setShowNew(false);
      setShowConfirm(false);
    }
  }, [visible]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleNewChange = (val: string) => {
    setNewPassword(val);
    if (newPassError) setNewPassError(validatePassword(val));
    if (confirmPassword && confirmError) setConfirmError(validateConfirm(val, confirmPassword));
  };

  const handleConfirmChange = (val: string) => {
    setConfirmPassword(val);
    if (confirmError) setConfirmError(validateConfirm(newPassword, val));
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    const pErr = validatePassword(newPassword);
    const cErr = validateConfirm(newPassword, confirmPassword);
    setNewPassError(pErr);
    setConfirmError(cErr);
    if (pErr || cErr) return;

    try {
      setIsSubmitting(true);
      await onSubmit(newPassword);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Password reset successfully!', life: 4000 });
      onClose();
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to reset password. Please try again.', life: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const strength = getStrength(newPassword);

  // ── Header ────────────────────────────────────────────────────────────────
  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '6px 0' }}>
      <div style={{
        width: 46, height: 46, borderRadius: 12,
        background: 'linear-gradient(135deg, #1e3a8a 0%, #2855AE 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, border: '3px solid white',
        boxShadow: '0 6px 18px rgba(40,85,174,0.25)',
      }}>
        <i className="pi pi-lock" style={{ fontSize: 20, color: 'white' }} />
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af', lineHeight: 1.2 }}>
          Reset Password
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500, marginTop: 2 }}>
          for {studentName}
        </div>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        .pw-reset-dialog .p-dialog-mask {
          backdrop-filter: blur(4px);
          background-color: rgba(0,0,0,0.3) !important;
          animation: pwMaskIn 0.3s ease;
        }
        @keyframes pwMaskIn { from { opacity:0 } to { opacity:1 } }
        .pw-reset-dialog .p-dialog {
          animation: pwDialogIn 0.3s cubic-bezier(0.4,0,0.2,1);
          box-shadow: 0 25px 60px rgba(0,0,0,0.2) !important;
        }
        @keyframes pwDialogIn { from { opacity:0; transform:scale(0.93) translateY(-16px) } to { opacity:1; transform:scale(1) translateY(0) } }
        .strength-seg { flex:1; height:4px; border-radius:2px; transition:background 0.3s; }
      `}</style>

      <Dialog
        className="pw-reset-dialog"
        visible={visible}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: 440, borderRadius: 20, overflow: 'hidden' }}
        contentStyle={{
          padding: 20,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: '0 0 20px 20px',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '18px 22px',
          borderRadius: '20px 20px 0 0',
        }}
        modal
        draggable={false}
        resizable={false}
        blockScroll
        dismissableMask={!isSubmitting}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── New Password ── */}
          <div>
            <GlobalInputField
              label="New Password"
              value={newPassword}
              onChange={e => handleNewChange(e.target.value)}
              placeholder="Enter new password"
              required
              disabled={isSubmitting}
              type={showNew ? 'text' : 'password'}
              icon={showNew ? 'pi pi-eye-slash' : 'pi pi-eye'}
              error={newPassError}
              autoComplete="new-password"
            />

            {/* Strength bar */}
            {newPassword.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6 }}>
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="strength-seg"
                    style={{ background: i <= strength.level ? strength.color : '#e5e7eb' }}
                  />
                ))}
                <span style={{ fontSize: 11, fontWeight: 700, color: strength.color, marginLeft: 4, minWidth: 38 }}>
                  {strength.label}
                </span>
              </div>
            )}
          </div>

          {/* ── Confirm Password ── */}
          <GlobalInputField
            label="Confirm Password"
            value={confirmPassword}
            onChange={e => handleConfirmChange(e.target.value)}
            placeholder="Confirm new password"
            required
            disabled={isSubmitting}
            type={showConfirm ? 'text' : 'password'}
            icon={showConfirm ? 'pi pi-eye-slash' : 'pi pi-eye'}
            error={confirmError}
            autoComplete="new-password"
          />

          {/* ── Requirements Card ── */}
          <div style={{
            background: 'rgba(239,246,255,0.7)', borderRadius: 12, padding: '14px 16px',
            border: '1px solid rgba(59,130,246,0.15)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{
                width: 26, height: 26, borderRadius: 7,
                background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className="pi pi-shield" style={{ fontSize: 13, color: '#2855AE' }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#2855AE', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Password Requirements
              </span>
            </div>
            {REQUIREMENTS.map((r, i) => {
              const met = r.met(newPassword);
              return (
                <div key={i} className="pw-req-row">
                  <i
                    className={met ? 'pi pi-check-circle' : 'pi pi-circle'}
                    style={{ fontSize: 13, color: met ? '#22C55E' : '#9ca3af', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 12, color: met ? '#16a34a' : '#6b7280', fontWeight: 500 }}>
                    {r.text}
                  </span>
                </div>
              );
            })}
          </div>

          {/* ── Submit ── */}
          <div style={{ textAlign: 'center', marginTop: 4 }}>
            <GlobalButton
              label={isSubmitting ? 'Resetting…' : 'Reset Password'}
              onClick={handleSubmit}
              variant="primary"
              size="medium"
              disabled={isSubmitting || !newPassword || !confirmPassword}
              loading={isSubmitting}
              fullWidth={false}
            />
          </div>

        </div>
      </Dialog>
    </>
  );
};

export default PasswordResetModal;

// ─────────────────────────────────────────────────────────────────────────────
// Usage example (in your StudentsListDashboard or ViewStudentModal):
// ─────────────────────────────────────────────────────────────────────────────
//
// import PasswordResetModal from './PasswordResetModal';
//
// const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
// const [targetStudent, setTargetStudent] = useState<StudentData | null>(null);
//
// const handlePasswordReset = async (newPassword: string) => {
//   await apiClient.post('/reset-student-password', {
//     user_id: targetStudent?.user_id,
//     password: newPassword,
//   });
// };
//
// <PasswordResetModal
//   visible={isPasswordModalOpen}
//   onClose={() => setIsPasswordModalOpen(false)}
//   onSubmit={handlePasswordReset}
//   studentName={targetStudent?.name ?? ''}
// />
//
// Then in your actionTemplate, add a reset button:
// <Button
//   icon="pi pi-key"
//   className="p-button-text p-button-rounded"
//   tooltip="Reset Password"
//   tooltipOptions={{ position: 'top' }}
//   style={{ color: '#8b5cf6', width: 32, height: 32 }}
//   onClick={() => { setTargetStudent(rowData); setIsPasswordModalOpen(true); }}
// />